import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import { useApp } from '../../context/AppContext';
import { MapPin, Heart, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import L from 'leaflet';

// Custom marker icons
const regularMarkerIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const selectedMarkerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [35, 57],
  iconAnchor: [17, 57],
  popupAnchor: [1, -48],
  shadowSize: [57, 57]
});

function SpotMarker({ spot, index, isSelected }) {
  const { state, actions } = useApp();

  const handleMarkerClick = () => {
    actions.setSelectedSpot(spot);
    actions.setCurrentSpotIndex(index);
    actions.setCurrentPhotoIndex(0);
    actions.setSelectedSpotFromPin(spot);
    
    // Center map on selected spot
    if (actions.state?.mapRef) {
      actions.state.mapRef.setView([spot.latitude, spot.longitude], 12);
    }
  };

  const handleShowDetails = () => {
    actions.setSelectedSpot(spot);
    actions.setShowSpotDetails(true);
  };

  const calculateDistance = () => {
    const { state } = useApp();
    if (state.userLocation && spot.latitude && spot.longitude) {
      const R = 6371; // Earth's radius in km
      const dLat = (spot.latitude - state.userLocation[0]) * Math.PI / 180;
      const dLon = (spot.longitude - state.userLocation[1]) * Math.PI / 180;
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(state.userLocation[0] * Math.PI / 180) * Math.cos(spot.latitude * Math.PI / 180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c;
      return Math.round(distance * 10) / 10; // Round to 1 decimal
    }
    return null; // Return null instead of random distance when no user location
  };

  const distance = calculateDistance();

  return (
    <Marker
      position={[spot.latitude, spot.longitude]}
      icon={isSelected ? selectedMarkerIcon : regularMarkerIcon}
      eventHandlers={{
        click: handleMarkerClick,
      }}
    >
      <Popup className="custom-popup" closeButton={false}>
        <div className="p-3 min-w-[250px]">
          {/* Header with image and title */}
          <div className="flex items-start space-x-3 mb-3">
            {spot.photos && spot.photos[0] && (
              <img
                src={spot.photos[0].url}
                alt={spot.title}
                className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-800 text-base leading-tight mb-1">
                {spot.title}
              </h3>
              <p className="text-gray-600 text-sm leading-tight line-clamp-2">
                {spot.description}
              </p>
            </div>
          </div>

          {/* Tags */}
          {spot.tags && spot.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {spot.tags.slice(0, 3).map((tag, idx) => (
                <span
                  key={idx}
                  className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs"
                >
                  #{tag}
                </span>
              ))}
              {spot.tags.length > 3 && (
                <span className="text-gray-500 text-xs">+{spot.tags.length - 3}</span>
              )}
            </div>
          )}

          {/* Stats and info */}
          <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <Heart size={14} className="text-red-500" />
                <span>{spot.likes || 0}</span>
              </div>
              <div className="flex items-center space-x-1">
                <MapPin size={14} className="text-blue-500" />
                <span>{distance ? `${distance} km` : 'Position inconnue'}</span>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              par {spot.author || 'Anonyme'}
            </div>
          </div>

          {/* Action button */}
          <Button
            onClick={handleShowDetails}
            size="sm"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Eye size={14} className="mr-1" />
            Plus de détails
          </Button>
        </div>
      </Popup>
    </Marker>
  );
}

export default SpotMarker;